import type { PDFDocument, PDFFont, PDFImage, PDFPage, RGB } from 'pdf-lib'

import { DeviceDetailType } from './deviceDetail.types'

/**
 * Works with 장비 상세를 PDF 문서로 굽는다 — 모달의 Download PDF (SITEMAP_PLAN §22.11).
 *
 * 화면의 표를 그림으로 찍지 않고 값에서 다시 조판한다. 화면은 스크롤되는 목록이고 문서는 쪽이 있는
 * 인쇄물이라 필요한 것이 다르다 — 쪽마다 열 제목이 다시 서고, 쪽 번호와 출처가 붙고, 로고 워터마크가
 * 깔린다. 그래서 html2canvas 류가 아니라 `pdf-lib` 로 직접 그린다(이미 있는 의존성 — PdfReader 가 쓴다).
 *
 * 서체는 PDF 표준 14 (Helvetica · Courier). 사이트 본문은 Archivo 지만 그걸 실으려면 TTF 를 받아
 * 임베드해야 하고, 문서 한 장에 400 KB 를 더 얹을 이유가 없다. hex 는 자릿수가 맞아야 읽히므로 Courier.
 */

// A4 세로 (pt)
const PAGE_W = 595.28
const PAGE_H = 841.89
const MARGIN_X = 56
const MARGIN_TOP = 46
const MARGIN_BOTTOM = 50
const CONTENT_W = PAGE_W - MARGIN_X * 2

/**
 * 표의 조밀도. 9pt 본문에 행 높이 약 18pt(비 2.0) — 자료 표로서 촘촘하되 줄이 붙지 않는 값이다.
 * 행 = 글줄들(size × LEADING) + ROW_PAD, 아래 헤어라인은 행 바닥에서 RULE_OFFSET 만큼 위.
 * RULE_OFFSET 은 곧 "선에서 다음 행 베이스라인까지" 라 이 값이 작아지면 글자가 선에 닿는다.
 */
const LEADING = 1.25
const ROW_PAD = 7
const RULE_OFFSET = 9

// 사이트 토큰과 같은 값 (monolith.tokens.ts). 0~1 스케일은 rgb() 로 만든다.
const INK = [0.086, 0.094, 0.106] as const
const DIM = [0.373, 0.4, 0.439] as const
const FAINT = [0.604, 0.631, 0.667] as const
const LINE = [0.898, 0.906, 0.918] as const

const LOGO_LOCKUP = '/images/logo/logo_title.png'
const LOGO_MARK = '/images/logo/logo.png'

interface FontSetType {
	regular: PDFFont
	bold: PDFFont
	mono: PDFFont
}

interface ColumnType {
	header: string
	width: number
	/** 이 열의 한 셀을 줄 배열로 만든다 — 이름 아래 설명처럼 두 줄짜리도 여기서 만든다 */
	lines: (row: never) => { text: string; font: PDFFont; size: number; color: RGB }[]
}

/**
 * PDF 표준 14 서체는 **WinAnsi(CP1252)** 만 싣는다 — 그 밖의 글자가 하나라도 있으면 pdf-lib 가 던진다.
 * 딕셔너리에는 편찬자가 남긴 기호(‐ → ≥)와 한국어 메모가 섞여 있어서(60 대 · 423 행, SITEMAP_PLAN §22.11)
 * 문서를 굽기 전에 한 번 훑는다.
 *
 * ① 뜻이 같은 기호는 ASCII 로 바꾼다 — 무손실. ② 그래도 못 싣는 글자는 덜어낸다.
 * ②에 걸리는 것은 전부 자유 서술(설명)이고 CC 번호·이름·범위·메시지는 ASCII 라 표의 값은 온전하다.
 * 근본 해결은 정본 딕셔너리에서 내부 메모를 걷어내는 것이다(그 메모는 지금 웹 화면에도 보인다).
 */
const SYMBOL_MAP: [RegExp, string][] = [
	[/[\u2010\u2011\u2012]/g, '-'],
	[/\u2192/g, '->'],
	[/\u2190/g, '<-'],
	[/\u2265/g, '>='],
	[/\u2264/g, '<='],
	[/\u2260/g, '!='],
	[/[\uFF5E\u301C]/g, '~'],
	[/\u00A0/g, ' '],
]

// WinAnsi 에 있는 글자 = ASCII + Latin-1 상단 + CP1252 의 0x80~0x9F 자리
const NON_WINANSI =
	/[^\u0020-\u007E\u00A0-\u00FF\u20AC\u201A\u0192\u201E\u2026\u2020\u2021\u02C6\u2030\u0160\u2039\u0152\u017D\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02DC\u2122\u0161\u203A\u0153\u017E\u0178]/g

const toWinAnsi = (value: string) => {
	let out = String(value)
	SYMBOL_MAP.forEach(([pattern, replacement]) => {
		out = out.replace(pattern, replacement)
	})
	return out
		.replace(NON_WINANSI, '')
		.replace(/\s{2,}/g, ' ')
		.trim()
}

/** 문서에 들어가는 문자열을 전부 한 번에 통과시킨다 — 그리는 지점마다 부르면 빠뜨린다. */
const toPrintable = (detail: DeviceDetailType): DeviceDetailType => ({
	...detail,
	manufacturer: toWinAnsi(detail.manufacturer),
	name: toWinAnsi(detail.name),
	cc: detail.cc.map(aRow => ({
		...aRow,
		cc: typeof aRow.cc === 'number' ? aRow.cc : toWinAnsi(aRow.cc),
		name: toWinAnsi(aRow.name),
		range: toWinAnsi(aRow.range),
		description: toWinAnsi(aRow.description),
	})),
	sysex: detail.sysex.map(aRow => ({
		...aRow,
		name: toWinAnsi(aRow.name),
		range: toWinAnsi(aRow.range),
		message: toWinAnsi(aRow.message),
		description: toWinAnsi(aRow.description),
	})),
})

const slugify = (value: string) =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')

const fmt = (n: number) => n.toLocaleString('en-US')

/** 오늘 날짜 — 로컬 기준 YYYY-MM-DD */
const today = () => {
	const d = new Date()
	return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

/**
 * 자간을 준 대문자 라벨. pdf-lib 에는 문자 간격 옵션이 없어 글자를 하나씩 놓는다.
 * 키커·열 제목·푸터에만 쓴다(짧은 문자열).
 */
const trackedWidth = (text: string, font: PDFFont, size: number, tracking: number) => {
	if (text === '') return 0
	return (
		Array.from(text).reduce(
			(w, ch) => w + font.widthOfTextAtSize(ch, size) + tracking,
			0,
		) - tracking
	)
}

/** maxWidth 안으로 접는다. 한 낱말이 폭보다 길면 글자 단위로 자른다(긴 hex·URL). */
const wrapText = (
	text: string,
	font: PDFFont,
	size: number,
	maxWidth: number,
): string[] => {
	const source = text.replace(/\s+/g, ' ').trim()
	if (source === '') return []

	const lines: string[] = []
	let line = ''

	const pushWord = (word: string) => {
		const candidate = line === '' ? word : `${line} ${word}`
		if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
			line = candidate
			return
		}
		if (line !== '') {
			lines.push(line)
			line = ''
		}
		if (font.widthOfTextAtSize(word, size) <= maxWidth) {
			line = word
			return
		}
		// 낱말 하나가 폭을 넘는다 — 글자 단위로 끊는다
		let chunk = ''
		Array.from(word).forEach(ch => {
			if (font.widthOfTextAtSize(chunk + ch, size) > maxWidth && chunk !== '') {
				lines.push(chunk)
				chunk = ''
			}
			chunk += ch
		})
		line = chunk
	}

	source.split(' ').forEach(pushWord)
	if (line !== '') lines.push(line)
	return lines
}

export const deviceDocumentFileName = (detail: DeviceDetailType) =>
	`judahs-works-with-${slugify(`${detail.manufacturer} ${detail.name}`)}.pdf`

/**
 * 문서를 만든다. 자산(로고 2장)은 바이트로 받는다 — 브라우저 fetch 는 부르는 쪽이 한다.
 */
export const buildDeviceDocument = async (
	source: DeviceDetailType,
	assets: { lockup: ArrayBuffer; mark: ArrayBuffer },
): Promise<Uint8Array> => {
	const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')

	const detail = toPrintable(source)

	const ink = rgb(...INK)
	const dim = rgb(...DIM)
	const faint = rgb(...FAINT)
	const line = rgb(...LINE)

	const pdf: PDFDocument = await PDFDocument.create()
	pdf.setTitle(`${detail.manufacturer} ${detail.name} — MIDI parameters`)
	pdf.setAuthor('Judahs')
	pdf.setSubject('MIDI Control Change and System Exclusive parameters')
	pdf.setCreator('judahs.io')
	pdf.setProducer('judahs.io')

	const fonts: FontSetType = {
		regular: await pdf.embedFont(StandardFonts.Helvetica),
		bold: await pdf.embedFont(StandardFonts.HelveticaBold),
		mono: await pdf.embedFont(StandardFonts.Courier),
	}
	const lockup: PDFImage = await pdf.embedPng(assets.lockup)
	const mark: PDFImage = await pdf.embedPng(assets.mark)

	const pages: PDFPage[] = []
	// newPage() 가 반드시 먼저 불린다 — TS 는 함수 안의 대입을 못 보므로 definite assignment.
	let page!: PDFPage
	let y = 0

	const drawTracked = (
		text: string,
		x: number,
		baseline: number,
		size: number,
		color: RGB,
		tracking: number,
		font: PDFFont = fonts.regular,
	) => {
		let cx = x
		Array.from(text).forEach(ch => {
			page.drawText(ch, { x: cx, y: baseline, size, font, color })
			cx += font.widthOfTextAtSize(ch, size) + tracking
		})
	}

	const rule = (at: number) => {
		page.drawLine({
			start: { x: MARGIN_X, y: at },
			end: { x: PAGE_W - MARGIN_X, y: at },
			thickness: 0.5,
			color: line,
		})
	}

	// 새 쪽 — 워터마크를 먼저 깔고(내용 뒤로 가게) 이어지는 쪽에는 러닝 헤드를 올린다.
	const newPage = () => {
		page = pdf.addPage([PAGE_W, PAGE_H])
		pages.push(page)

		const markSize = 300
		page.drawImage(mark, {
			x: (PAGE_W - markSize) / 2,
			y: (PAGE_H - markSize) / 2,
			width: markSize,
			height: markSize,
			opacity: 0.04,
		})

		y = PAGE_H - MARGIN_TOP

		if (pages.length > 1) {
			drawTracked(
				`${detail.manufacturer} ${detail.name}`.toUpperCase(),
				MARGIN_X,
				y - 8,
				7.5,
				faint,
				1.1,
			)
			rule(y - 16)
			y -= 32
		}
	}

	const ensure = (needed: number) => {
		if (y - needed < MARGIN_BOTTOM) newPage()
	}

	newPage()

	// ── 표제부 ────────────────────────────────────────────────────────────────
	const lockupW = 104
	const lockupH = (lockup.height / lockup.width) * lockupW
	page.drawImage(lockup, { x: MARGIN_X, y: y - lockupH, width: lockupW, height: lockupH })

	const stamp = `GENERATED ${today()}`
	drawTracked(
		stamp,
		PAGE_W - MARGIN_X - trackedWidth(stamp, fonts.regular, 7.5, 1.1),
		y - lockupH + 2,
		7.5,
		faint,
		1.1,
	)

	y -= lockupH + 20
	rule(y)
	y -= 24

	drawTracked(detail.manufacturer.toUpperCase(), MARGIN_X, y, 8, faint, 1.6)
	y -= 26
	page.drawText(detail.name, {
		x: MARGIN_X,
		y,
		size: 24,
		font: fonts.regular,
		color: ink,
	})
	y -= 18

	const facts = [
		`${fmt(detail.cc.length)} Control Change`,
		`${fmt(detail.sysex.length)} System Exclusive`,
		`Dictionary updated ${
			detail.cc.length > 0 ? detail.updated.cc : detail.updated.sysex
		}`,
	].join('   ·   ')
	page.drawText(facts, { x: MARGIN_X, y, size: 9, font: fonts.regular, color: dim })
	y -= 16
	rule(y)
	y -= 26

	// ── 표 ────────────────────────────────────────────────────────────────────
	const drawTable = (
		title: string,
		count: number,
		columns: ColumnType[],
		rows: unknown[],
	) => {
		ensure(84)

		page.drawText(title, { x: MARGIN_X, y, size: 13, font: fonts.regular, color: ink })
		const countLabel = `${fmt(count)} ${count === 1 ? 'ENTRY' : 'ENTRIES'}`
		drawTracked(
			countLabel,
			PAGE_W - MARGIN_X - trackedWidth(countLabel, fonts.regular, 7.5, 1.1),
			y + 2,
			7.5,
			faint,
			1.1,
		)
		y -= 18

		const header = () => {
			let x = MARGIN_X
			columns.forEach(aColumn => {
				drawTracked(aColumn.header.toUpperCase(), x, y, 7.5, faint, 1.1)
				x += aColumn.width
			})
			y -= 8
			rule(y)
			y -= 12
		}
		header()

		rows.forEach(aRow => {
			const cells = columns.map(aColumn => {
				const parts = aColumn.lines(aRow as never)
				const lines: { text: string; font: PDFFont; size: number; color: RGB }[] = []
				parts.forEach(aPart => {
					wrapText(aPart.text, aPart.font, aPart.size, aColumn.width - 12).forEach(text =>
						lines.push({ ...aPart, text }),
					)
				})
				return lines
			})

			const height = cells.reduce(
				(tallest, lines) =>
					Math.max(
						tallest,
						lines.reduce((sum, aLine) => sum + aLine.size * LEADING, 0),
					),
				0,
			)
			const rowHeight = height + ROW_PAD

			if (y - rowHeight < MARGIN_BOTTOM) {
				newPage()
				header()
			}

			let x = MARGIN_X
			cells.forEach((lines, idx) => {
				let ly = y
				lines.forEach(aLine => {
					page.drawText(aLine.text, {
						x,
						y: ly,
						size: aLine.size,
						font: aLine.font,
						color: aLine.color,
					})
					ly -= aLine.size * LEADING
				})
				x += columns[idx].width
			})

			y -= rowHeight
			rule(y + RULE_OFFSET)
		})

		y -= 20
	}

	if (detail.cc.length > 0) {
		const hasRange = detail.cc.some(aRow => aRow.range !== '')
		const hasDesc = detail.cc.some(aRow => aRow.description !== '')
		const numberW = 42
		const rangeW = hasRange ? 58 : 0
		const nameW = hasDesc ? 150 : CONTENT_W - numberW - rangeW
		const descW = CONTENT_W - numberW - rangeW - nameW

		const columns: ColumnType[] = [
			{
				header: 'CC',
				width: numberW,
				lines: (row: DeviceDetailType['cc'][number]) => [
					{ text: String(row.cc), font: fonts.bold, size: 8.5, color: ink },
				],
			},
			{
				header: 'Parameter',
				width: nameW,
				lines: (row: DeviceDetailType['cc'][number]) => [
					{ text: row.name, font: fonts.regular, size: 9, color: ink },
				],
			},
		]
		if (hasRange) {
			columns.push({
				header: 'Range',
				width: rangeW,
				lines: (row: DeviceDetailType['cc'][number]) => [
					{ text: row.range, font: fonts.regular, size: 9, color: dim },
				],
			})
		}
		if (hasDesc) {
			columns.push({
				header: 'Description',
				width: descW,
				lines: (row: DeviceDetailType['cc'][number]) =>
					row.description === ''
						? []
						: [{ text: row.description, font: fonts.regular, size: 8.5, color: dim }],
			})
		}
		drawTable('Control Change', detail.cc.length, columns, detail.cc)
	}

	if (detail.sysex.length > 0) {
		const hasRange = detail.sysex.some(aRow => aRow.range !== '')
		const rangeW = hasRange ? 52 : 0
		const nameW = 148
		const messageW = CONTENT_W - nameW - rangeW

		const columns: ColumnType[] = [
			{
				header: 'Parameter',
				width: nameW,
				// 설명은 열을 따로 두지 않고 이름 아래 한 줄 — 메시지 열이 넓어 4열이면 잘린다(화면과 같은 규칙).
				lines: (row: DeviceDetailType['sysex'][number]) => {
					const out = [{ text: row.name, font: fonts.regular, size: 9, color: ink }]
					if (row.description !== '') {
						out.push({
							text: row.description,
							font: fonts.regular,
							size: 7.5,
							color: faint,
						})
					}
					return out
				},
			},
		]
		if (hasRange) {
			columns.push({
				header: 'Range',
				width: rangeW,
				lines: (row: DeviceDetailType['sysex'][number]) => [
					{ text: row.range, font: fonts.regular, size: 9, color: dim },
				],
			})
		}
		columns.push({
			header: 'Message',
			width: messageW,
			lines: (row: DeviceDetailType['sysex'][number]) => [
				{ text: row.message, font: fonts.mono, size: 7.5, color: ink },
			],
		})
		drawTable('System Exclusive', detail.sysex.length, columns, detail.sysex)
	}

	// ── 맺음 한 줄 — 모달의 문장 그대로 ────────────────────────────────────────
	const note =
		'Values are as the device documents them. Any of them goes out from a footswitch, an expression pedal, or a scene.'
	const noteLines = wrapText(note, fonts.regular, 9, CONTENT_W - 140)
	// 표가 이미 헤어라인으로 끝난다 — 여기에 선을 한 번 더 그으면 두 줄이 된다.
	ensure(noteLines.length * 12 + 20)
	noteLines.forEach(aLine => {
		page.drawText(aLine, { x: MARGIN_X, y, size: 9, font: fonts.regular, color: dim })
		y -= 12
	})

	// ── 푸터 — 쪽 수를 알아야 하므로 마지막에 전 쪽을 돈다 ────────────────────
	pages.forEach((aPage, idx) => {
		const left = 'JUDAHS  ·  JUDAHS.IO/WORKS-WITH'
		let cx = MARGIN_X
		Array.from(left).forEach(ch => {
			aPage.drawText(ch, {
				x: cx,
				y: MARGIN_BOTTOM - 26,
				size: 7,
				font: fonts.regular,
				color: faint,
			})
			cx += fonts.regular.widthOfTextAtSize(ch, 7) + 1
		})

		const right = `${idx + 1} / ${pages.length}`
		aPage.drawText(right, {
			x: PAGE_W - MARGIN_X - fonts.regular.widthOfTextAtSize(right, 7.5),
			y: MARGIN_BOTTOM - 26,
			size: 7.5,
			font: fonts.regular,
			color: faint,
		})
		aPage.drawLine({
			start: { x: MARGIN_X, y: MARGIN_BOTTOM - 14 },
			end: { x: PAGE_W - MARGIN_X, y: MARGIN_BOTTOM - 14 },
			thickness: 0.5,
			color: line,
		})
	})

	return pdf.save()
}

/** 브라우저에서 자산을 받아 문서를 만들고 내려받는다. */
export const downloadDeviceDocument = async (detail: DeviceDetailType) => {
	const load = async (path: string) => {
		const res = await fetch(path)
		if (!res.ok) throw new Error(`${path} ${res.status}`)
		return res.arrayBuffer()
	}
	const [lockup, mark] = await Promise.all([load(LOGO_LOCKUP), load(LOGO_MARK)])
	const bytes = await buildDeviceDocument(detail, { lockup, mark })

	const blob = new Blob([bytes], { type: 'application/pdf' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = deviceDocumentFileName(detail)
	document.body.appendChild(a)
	a.click()
	a.remove()
	window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
